/*
 *  Copyright (c) 2024 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Config } from '../config'
import { makeRequest } from '../common/index'
import qs from 'qs'

const base_url = Config.admin.api_base_url
const context = Config.admin.context
const endpoint = `/${context}/media/documents`

/**
 *  getDocuments
 *  @param {string} token
 *  @param {Object} params
 */
export const getDocuments = async function (token, params) {
    const method = 'GET'
    let path = `${endpoint}`
    if (params) {
        const page = params.page
        const pageSize = params.pageSize
        const query = qs.stringify(
            { pagination: { page, pageSize } }, { encodeValuesOnly: true }
        )
        path = `${path}?${query}`
    }
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  searchDocuments
 *  @param {string} token
 *  @param {string} searchTerm
 *  @param {Object} params
 */
export const searchDocuments = async function (token, searchTerm, params) {
    const method = 'GET'
    let path = `${endpoint}/search`
    if (params) {
        const page = params.page
        const pageSize = params.pageSize
        const query = qs.stringify(
            {
                term: searchTerm,
                pagination: { page, pageSize }
            },
            { encodeValuesOnly: true }
        )
        path = `${path}?${query}`
    }
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getDocument
 *  @param {string} token
 *  @param {string} guid
 */
export const getDocument = async function (token, guid) {
    const method = 'GET'
    let path = `${endpoint}/${guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getDocumentByFilePath
 *  @param {string} token
 *  @param {string} filepath
 */
export const getDocumentByFilePath = async function (token, filepath) {
    const method = 'GET'
    let path = `${endpoint}/filepath/${filepath}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  createDocument
 *  @param {string} token
 *  @param {Object} payload
 */
export const createDocument = async function (token, payload) {
    const method = 'POST'
    let path = `${endpoint}`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  updateDocument
 *  @param {string} token
 *  @param {string} guid
 *  @param {Object} payload
 */
export const updateDocument = async function (token, guid, payload) {
    const method = 'PUT'
    let path = `${endpoint}/${guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  deleteDocument
 *  @param {string} token
 *  @param {string} guid
 */
export const deleteDocument = async function (token, guid) {
    const method = 'DELETE'
    const path = `${endpoint}/${guid}`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  archiveDocument
 *  @param {string} token
 *  @param {string} guid
 */
export const archiveDocument = async function (token, guid) {
    const method = 'PUT'
    const path = `${endpoint}/${guid}/archive`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  renameDocument
 *  @param {string} token
 *  @param {string} guid
 *  @param {string} renamed_file_name
 */
export const renameDocument = async function (token, guid, renamed_file_name) {
    const method = 'POST'
    const path = `${endpoint}/${guid}/rename`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = { renamed_file_name }
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  purgeAllDocuments
 *  @param {string} token
 */
export const purgeAllDocuments = async function (token) {
    const method = 'POST'
    const path = `${endpoint}/purge/all`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  purgeDocuments
 *  @param {string} token
 *  @param {Array<{string}>} paths
 */
export const purgeDocuments = async function (token, paths) {
    const method = 'POST'
    const path = `${endpoint}/purge`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = { paths }
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  getDocumentPlacements
 *  @param {string} token
 *  @param {string} guid
 */
export const getDocumentPlacements = async function (token, guid) {
    const method = 'GET'
    let path = `${endpoint}/${guid}/placements`
    const headers = { 'Authorization': `Bearer ${token}` }
    const payload = undefined
    return await makeRequest(method, base_url, path, headers, payload)
}

/**
 *  fileExist
 *  @param {string} token
 */
export const fileExist = async function (token, file_name) {
    const method = 'POST'
    const path = `${endpoint}/fileExist`
    const headers = {"Authorization": `Bearer ${token}`}
    const payload = { file_name }
    return await makeRequest(method, base_url, path, headers, payload)
}
